import React from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  SimpleGrid,
  Stack,
  Icon,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon
} from '@chakra-ui/react';
import { FaServer, FaDatabase, FaGlobe, FaShieldAlt, FaLock, FaUserShield } from 'react-icons/fa';
import MarketingFooter from './MarketingFooter';
import { marketingHeadingStyles } from './marketingStyles';

const SecurityPage = () => {
  return (
    <Box fontFamily="var(--telivi-font-body)" sx={marketingHeadingStyles}>
      <Box bgGradient="linear(to-b, gray.50, white)" pt={{ base: 16, lg: 24 }} pb={{ base: 12, lg: 16 }}>
        <Container maxW="container.xl">
          <Stack spacing={4} textAlign="center">
            <Text color="blue.500" fontWeight="600" textTransform="uppercase">
              Security & Isolation
            </Text>
            <Heading as="h1" fontSize={{ base: '3xl', md: '5xl' }} fontFamily="var(--telivi-font-heading)">
              True tenant isolation, built into every deployment
            </Heading>
            <Text color="gray.600" fontSize="lg">
              Each Telivi customer receives dedicated infrastructure, databases, and tenant-specific
              domains. No shared stacks, no shared databases.
            </Text>
          </Stack>
        </Container>
      </Box>

      <Box py={{ base: 16, md: 20 }}>
        <Container maxW="container.xl">
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
            <Box bg="white" borderRadius="2xl" p={6} boxShadow="sm" border="1px solid" borderColor="gray.100">
              <Icon as={FaServer} color="blue.500" boxSize={6} mb={4} />
              <Heading as="h2" fontSize="xl" mb={2}>
                Dedicated infrastructure
              </Heading>
              <Text color="gray.600">
                Each tenant runs on isolated compute, networking, and storage resources.
              </Text>
            </Box>
            <Box bg="white" borderRadius="2xl" p={6} boxShadow="sm" border="1px solid" borderColor="gray.100">
              <Icon as={FaDatabase} color="blue.500" boxSize={6} mb={4} />
              <Heading as="h2" fontSize="xl" mb={2}>
                Dedicated databases
              </Heading>
              <Text color="gray.600">
                Data never co-mingles with other tenants. Each platform uses its own database
                cluster.
              </Text>
            </Box>
            <Box bg="white" borderRadius="2xl" p={6} boxShadow="sm" border="1px solid" borderColor="gray.100">
              <Icon as={FaGlobe} color="blue.500" boxSize={6} mb={4} />
              <Heading as="h2" fontSize="xl" mb={2}>
                Tenant-specific domains
              </Heading>
              <Text color="gray.600">
                Your team operates under its own domain and authentication boundary.
              </Text>
            </Box>
          </SimpleGrid>
        </Container>
      </Box>

      <Box py={{ base: 16, md: 20 }} bg="gray.50">
        <Container maxW="container.xl">
          <Stack spacing={4} textAlign="center" mb={10}>
            <Text color="blue.500" fontWeight="600" textTransform="uppercase">
              Security posture
            </Text>
            <Heading as="h2" fontSize={{ base: '2xl', md: '4xl' }} fontFamily="var(--telivi-font-heading)">
              Practical controls, shared responsibility
            </Heading>
            <Text color="gray.600" fontSize="lg">
              Telivi maintains a secure baseline, while customers control access, users, and
              operational policies for their tenant.
            </Text>
          </Stack>
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
            <Box bg="white" borderRadius="2xl" p={6} boxShadow="sm" border="1px solid" borderColor="gray.100">
              <Icon as={FaLock} color="blue.500" boxSize={6} mb={4} />
              <Heading as="h3" fontSize="lg" mb={2}>
                Encryption & backups
              </Heading>
              <Text color="gray.600">
                Data is encrypted in transit and at rest with routine backups and recovery plans.
              </Text>
            </Box>
            <Box bg="white" borderRadius="2xl" p={6} boxShadow="sm" border="1px solid" borderColor="gray.100">
              <Icon as={FaShieldAlt} color="blue.500" boxSize={6} mb={4} />
              <Heading as="h3" fontSize="lg" mb={2}>
                Monitoring & incident response
              </Heading>
              <Text color="gray.600">
                We monitor platform health and provide incident response guidance when needed.
              </Text>
            </Box>
            <Box bg="white" borderRadius="2xl" p={6} boxShadow="sm" border="1px solid" borderColor="gray.100">
              <Icon as={FaUserShield} color="blue.500" boxSize={6} mb={4} />
              <Heading as="h3" fontSize="lg" mb={2}>
                Access controls
              </Heading>
              <Text color="gray.600">
                Role-based access, audit logs, and SSO options keep teams aligned with policy.
              </Text>
            </Box>
          </SimpleGrid>
        </Container>
      </Box>

      <Box py={{ base: 16, md: 20 }}>
        <Container maxW="container.xl">
          <Stack spacing={4} textAlign="center" mb={8}>
            <Text color="blue.500" fontWeight="600" textTransform="uppercase">
              FAQ
            </Text>
            <Heading as="h2" fontSize={{ base: '2xl', md: '4xl' }} fontFamily="var(--telivi-font-heading)">
              Common isolation questions
            </Heading>
          </Stack>
          <Accordion allowToggle>
            <AccordionItem>
              <h3>
                <AccordionButton>
                  <Box flex="1" textAlign="left">
                    Is my data shared with other tenants?
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
              </h3>
              <AccordionPanel pb={4}>
                No. Each tenant has its own dedicated databases and deployment environment, keeping
                data isolated by design.
              </AccordionPanel>
            </AccordionItem>
            <AccordionItem>
              <h3>
                <AccordionButton>
                  <Box flex="1" textAlign="left">
                    How are custom domains handled?
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
              </h3>
              <AccordionPanel pb={4}>
                We provision tenant-specific domains during onboarding and configure SSL and routing
                for your environment.
              </AccordionPanel>
            </AccordionItem>
            <AccordionItem>
              <h3>
                <AccordionButton>
                  <Box flex="1" textAlign="left">
                    Does Telivi guarantee absolute security?
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
              </h3>
              <AccordionPanel pb={4}>
                We implement strong security controls and monitoring, but security is a shared
                responsibility between Telivi and your team.
              </AccordionPanel>
            </AccordionItem>
            <AccordionItem>
              <h3>
                <AccordionButton>
                  <Box flex="1" textAlign="left">
                    Can we use Clerk authentication?
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
              </h3>
              <AccordionPanel pb={4}>
                Yes. We can integrate Clerk for authentication and user management as part of your
                onboarding configuration.
              </AccordionPanel>
            </AccordionItem>
            <AccordionItem>
              <h3>
                <AccordionButton>
                  <Box flex="1" textAlign="left">
                    Are microservices isolated and fast?
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
              </h3>
              <AccordionPanel pb={4}>
                Each platform runs isolated services with dedicated resources. Performance tuning and
                service boundaries are designed for low latency and predictable throughput.
              </AccordionPanel>
            </AccordionItem>
            <AccordionItem>
              <h3>
                <AccordionButton>
                  <Box flex="1" textAlign="left">
                    Do you support Cloudflare routing and security?
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
              </h3>
              <AccordionPanel pb={4}>
                Yes. Cloudflare routing, SSL, and edge security can be configured per tenant during
                onboarding.
              </AccordionPanel>
            </AccordionItem>
            <AccordionItem>
              <h3>
                <AccordionButton>
                  <Box flex="1" textAlign="left">
                    How does scaling on demand work?
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
              </h3>
              <AccordionPanel pb={4}>
                We scale your isolated infrastructure based on usage patterns and agreed thresholds,
                ensuring performance without sharing resources across tenants.
              </AccordionPanel>
            </AccordionItem>
          </Accordion>
        </Container>
      </Box>

      <MarketingFooter />
    </Box>
  );
};

export default SecurityPage;
